import React from 'react'
import { UserService } from '@duck/api-definitions'
import { Badge, CopyableText, MultiPick, Switch, Textarea } from '@duck/ui'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { UserAvatar } from '../../UserAvatar'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { OrganizationDataInput } from '@/components/DataInputs/OrganizationDataInput'
import { getRoleBadgeColor } from '../utils'

const ROLE_OPTIONS = [
  { value: 'user', label: 'common.user' },
  { value: 'partner', label: 'common.partner' },
  { value: 'admin', label: 'common.admin' },
] as const

const SOURCE_OPTIONS: { value: UserService.UserSource; label: TranslationKey }[] = [
  { value: UserService.UserSource.Organic, label: 'users.sourceOrganic' },
  { value: UserService.UserSource.Invited, label: 'users.sourceInvited' },
  { value: UserService.UserSource.PartnerCandidate, label: 'users.sourcePartnerCandidate' },
  { value: UserService.UserSource.PartnerStudent, label: 'users.sourcePartnerStudent' },
  { value: UserService.UserSource.PublicCandidate, label: 'users.sourcePublicCandidate' },
  { value: UserService.UserSource.PublicStudent, label: 'users.sourcePublicStudent' },
]

const BADGE_OPTIONS: { value: UserService.UserBadge; label: TranslationKey }[] = [
  { value: UserService.UserBadge.AssignmentOnly, label: 'users.assignmentOnly' },
  { value: UserService.UserBadge.AlphaTester, label: 'users.alphaTester' },
  { value: UserService.UserBadge.HardcoreUser, label: 'users.hardcoreUser' },
]

const PROFESSION_OPTIONS: { value: string; label: TranslationKey }[] = [
  { value: 'data-scientist', label: 'personalInfo.professionOptions.dataScientist' },
  { value: 'data-analyst', label: 'personalInfo.professionOptions.dataAnalyst' },
  { value: 'data-engineer', label: 'personalInfo.professionOptions.dataEngineer' },
  { value: 'data-operations', label: 'personalInfo.professionOptions.dataOperations' },
  { value: 'machine-learning-engineer', label: 'personalInfo.professionOptions.machineLearningEngineer' },
  { value: 'software-engineer', label: 'personalInfo.professionOptions.softwareEngineer' },
  { value: 'recruitment-manager', label: 'personalInfo.professionOptions.recruitmentManager' },
  { value: 'other', label: 'common.other' },
]

const AUTH_PROVIDER_LABELS: Record<string, string> = {
  local: 'Email',
  google: 'Google',
  linkedin: 'LinkedIn',
}

interface OverviewSectionProps {
  formData: Partial<UserService.User>
  onChange: (field: keyof UserService.User, value: unknown) => void
  onPersonalInfoChange: (field: string, value: unknown) => void
  allAccessGroups: string[]
  user: UserService.User
  challenges: UserService.AdminUserChallenge[]
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  formData,
  onChange,
  allAccessGroups,
  user,
  challenges,
}) => {
  const { t } = useTar()

  const fullName =
    `${(formData.firstName as string | undefined) ?? user.firstName ?? ''} ${(formData.lastName as string | undefined) ?? user.lastName ?? ''
      }`.trim() || undefined

  const email = (formData.email as string | undefined) ?? user.email ?? ''
  const authProviderKey = (user as unknown as { authProvider?: string }).authProvider || 'local'
  const roleValue = formData.role ?? user.role
  const sourceValue =
    formData.source ?? user.source

  const roleLabel =
    ROLE_OPTIONS.find((opt) => opt.value === roleValue)?.label ?? ('common.na')

  const sourceLabel =
    SOURCE_OPTIONS.find((opt) => opt.value === sourceValue)?.label ?? ('common.na')

  const personalInfo = formData.personalInfo ?? user.personalInfo
  const hasPersonalInfo =
    !!personalInfo &&
    (personalInfo.yearsOfXP !== undefined ||
      !!personalInfo.profession ||
      !!personalInfo.country)

  const professionLabel =
    personalInfo?.profession &&
    (PROFESSION_OPTIONS.find((opt) => opt.value === personalInfo.profession)?.label ??
      ('common.na'))

  const totalChallenges = challenges.length
  const finishedChallenges = challenges.filter((challenge) => challenge.status === 'finished').length

  return (
    <div className="space-y-6">
      {/* User Overview */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('users.userDetails')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Identity card */}
          <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-4">
            <div className="flex gap-4 items-start">
              <UserAvatar size="lg" user={user} className="shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="flex flex-col text-start">
                  <span className="text-base font-semibold ">
                    {fullName}
                  </span>
                </div>
                <CopyableText
                  value={email}
                  className="justify-start px-0 py-0"
                >
                  <div className="flex flex-col text-start">
                    <span className="text-sm font-mono  break-all">
                      {email}
                    </span>
                  </div>
                </CopyableText>
                <CopyableText
                  value={user.id}
                  className="justify-start px-0 py-0"
                >
                  <div className="flex flex-col text-start">
                    <span className="text-xs font-mono  break-all">
                      {user.id}
                    </span>
                  </div>
                </CopyableText>
              </div>
            </div>

            <div className="space-y-2">
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => onChange('notes', e.target.value)}
                placeholder={t('common.notes')}
                rows={3}
              />
            </div>
          </div>

          {/* Right: Account meta */}
          <div className="bg-card border border-border rounded-lg p-4 text-sm">
            <div className="flex flex-col sm:flex-row gap-4 h-full">
              {/* Left column: dates */}
              <div className="flex flex-col flex-1 space-y-2">
                <div className="flex flex-col flex-1 justify-center space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('common.joined')}:
                  </span>
                  <span className="text-foreground">
                    {user.createdAt ? (
                      <RelativeDateTime isoDateString={user.createdAt} />
                    ) : (
                      t('common.na')
                    )}
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('common.lastLoginAt')}
                  </span>
                  <span className="text-foreground">
                    {user.lastLoginAt ? (
                      <RelativeDateTime isoDateString={user.lastLoginAt} />
                    ) : (
                      t('common.never')
                    )}
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('navigation.challenges')}
                  </span>
                  <span className="text-foreground">
                    {finishedChallenges}/{totalChallenges}
                  </span>
                </div>
              </div>

              {/* Right column: badges */}
              <div className="flex flex-col flex-1 space-y-2">
                <div className="flex flex-col flex-1 space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('common.role')}
                  </span>
                  <Badge
                    variant="outline"
                    className={`w-fit ${getRoleBadgeColor(roleValue)}`}
                  >
                    {roleValue ? t(roleLabel) : t('common.na')}
                  </Badge>
                </div>
                <div className="flex flex-col flex-1 space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('users.source')}
                  </span>
                  <Badge variant="outline" className="w-fit">
                    {sourceValue ? t(sourceLabel) : t('common.na')}
                  </Badge>
                </div>
                <div className="flex flex-col flex-1 space-y-1">
                  <span className="block text-xs font-medium text-muted-foreground">
                    {t('common.authProvider')}
                  </span>
                  <Badge variant="outline" className="w-fit">
                    {AUTH_PROVIDER_LABELS[authProviderKey] ?? authProviderKey}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Editable Fields */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">{t('organizations.organization')}</label>
            <OrganizationDataInput
              singlePick
              selected={formData.organizationId ? [formData.organizationId] : []}
              onChange={(vals) => onChange('organizationId', vals[0] ?? undefined)}
              labels={{
                placeholder: '',
                searchPlaceholder: t('common.search'),
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.accessGroups')} <span className="text-error ms-1">*</span>
            </label>
            <MultiPick
              options={allAccessGroups.map((g) => ({ label: g, value: g }))}
              selected={Array.isArray(formData.accessGroups) ? (formData.accessGroups as string[]) : []}
              onChange={(vals) => onChange('accessGroups', vals)}
              labels={{ placeholder: '' }}
              allowNewItem
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">{t('users.badges')}</label>
            <MultiPick
              options={BADGE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.label),
              }))}
              labels={{ placeholder: '' }}
              selected={Array.isArray(formData.badges) ? formData.badges : []}
              onChange={(vals) => onChange('badges', vals)}
            />
          </div>
          <div className="flex items-center gap-6 pt-6">
            <label className="flex items-center gap-2">
              <Switch
                checked={!!formData.isActive}
                onCheckedChange={(checked) => onChange('isActive', checked === true)}
              />
              <span className="text-sm text-foreground">{t('common.isActive')}</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={!!formData.isVerified}
                onCheckedChange={(checked) => onChange('isVerified', checked === true)}
              />
              <span className="text-sm text-foreground">{t('common.isVerified')}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Personal Info */}
      {hasPersonalInfo && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            {t('personalInfo.personalInfo')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
            <div className="space-y-1">
              <div className="block text-sm font-medium text-foreground">
                {t('personalInfo.yearsOfXP')}
              </div>
              <div className="text-foreground">
                {personalInfo?.yearsOfXP ?? t('common.na')}
              </div>
            </div>
            <div className="space-y-1">
              <div className="block text-sm font-medium text-foreground">
                {t('personalInfo.profession')}
              </div>
              <div className="text-foreground">
                {professionLabel ? t(professionLabel) : t('common.na')}
              </div>
            </div>
            <div className="space-y-1">
              <div className="block text-sm font-medium text-foreground">
                {t('common.country')}
              </div>
              <div className="text-foreground">
                {personalInfo?.country ?? t('common.na')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
